import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import {
  Plus, ChevronLeft, ChevronRight, Calendar as CalendarIcon,
  MapPin, Clock, Trash2, Edit, Building2, Landmark,
  Banknote, Wrench, Eye, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, getDay, addMonths, subMonths, isSameDay, isSameMonth, parseISO, getISOWeek, startOfWeek } from 'date-fns';
import { fr } from 'date-fns/locale';

const API = ((typeof window !== "undefined" && window.__API_URL__) || process.env.REACT_APP_BACKEND_URL) + '/api';

const TYPE_CONFIG = {
  visite: { label: 'Visite', color: '#10B981', icon: Eye },
  notaire: { label: 'Notaire', color: '#3B82F6', icon: Landmark },
  banque: { label: 'Banque', color: '#F59E0B', icon: Banknote },
  artisan: { label: 'Artisan', color: '#A855F7', icon: Wrench },
  autre: { label: 'Autre', color: '#71717A', icon: CalendarIcon },
};

const DAYS = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];

const AppointmentForm = ({ appointment, properties, onSave, onClose }) => {
  const [form, setForm] = useState({
    title: appointment?.title || '',
    date: appointment?.date || format(new Date(), 'yyyy-MM-dd'),
    time: appointment?.time || '09:00',
    end_time: appointment?.end_time || '10:00',
    type: appointment?.type || 'visite',
    property_id: appointment?.property_id || '',
    description: appointment?.description || '',
    location: appointment?.location || '',
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const data = { ...form, color: TYPE_CONFIG[form.type]?.color || '#71717A' };
      if (appointment?.id) {
        await axios.put(`${API}/appointments/${appointment.id}`, data, { withCredentials: true });
        toast.success('RDV mis a jour');
      } else {
        await axios.post(`${API}/appointments`, data, { withCredentials: true });
        toast.success('RDV cree');
      }
      onSave();
      onClose();
    } catch {
      toast.error('Erreur');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <Label className="text-zinc-300">Titre</Label>
        <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required className="bg-zinc-950 border-zinc-800 text-white" data-testid="appointment-title-input" />
      </div>
      <div className="grid grid-cols-3 gap-4">
        <div>
          <Label className="text-zinc-300">Date</Label>
          <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="bg-zinc-950 border-zinc-800 text-white" />
        </div>
        <div>
          <Label className="text-zinc-300">Debut</Label>
          <Input type="time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} className="bg-zinc-950 border-zinc-800 text-white" />
        </div>
        <div>
          <Label className="text-zinc-300">Fin</Label>
          <Input type="time" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} className="bg-zinc-950 border-zinc-800 text-white" />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label className="text-zinc-300">Type</Label>
          <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
            <SelectTrigger className="bg-zinc-950 border-zinc-800 text-white"><SelectValue /></SelectTrigger>
            <SelectContent>
              {Object.entries(TYPE_CONFIG).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-zinc-300">Bien associe</Label>
          <Select value={form.property_id} onValueChange={(v) => setForm({ ...form, property_id: v })}>
            <SelectTrigger className="bg-zinc-950 border-zinc-800 text-white"><SelectValue placeholder="Aucun" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Aucun</SelectItem>
              {properties.map((p) => <SelectItem key={p.id} value={p.id}>{p.title} - {p.city}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div>
        <Label className="text-zinc-300">Lieu</Label>
        <Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="bg-zinc-950 border-zinc-800 text-white" placeholder="Adresse du RDV" />
      </div>
      <div>
        <Label className="text-zinc-300">Notes</Label>
        <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="bg-zinc-950 border-zinc-800 text-white min-h-[60px]" />
      </div>
      <div className="flex justify-end gap-3 pt-2">
        <Button type="button" variant="outline" onClick={onClose} className="border-zinc-700 text-zinc-300">Annuler</Button>
        <Button type="submit" disabled={loading} className="bg-emerald-500 hover:bg-emerald-600 text-white" data-testid="save-appointment-button">
          {appointment?.id ? 'Modifier' : 'Creer'}
        </Button>
      </div>
    </form>
  );
};

export const CalendarPage = () => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [appointments, setAppointments] = useState([]);
  const [properties, setProperties] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingApt, setEditingApt] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    try {
      const month = currentMonth.getMonth() + 1;
      const year = currentMonth.getFullYear();
      const [aptRes, propRes] = await Promise.all([
        axios.get(`${API}/appointments?month=${month}&year=${year}`, { withCredentials: true }),
        axios.get(`${API}/properties`, { withCredentials: true })
      ]);
      setAppointments(aptRes.data);
      setProperties(propRes.data);
    } catch { /* ignore */ } finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, [currentMonth]);

  const days = useMemo(() => {
    const start = startOfMonth(currentMonth);
    const end = endOfMonth(currentMonth);
    const allDays = eachDayOfInterval({ start, end });
    const startDow = (getDay(start) + 6) % 7;
    const paddingBefore = Array(startDow).fill(null);
    return [...paddingBefore, ...allDays];
  }, [currentMonth]);

  // Group days into weeks (7-day chunks) to allow displaying week number on each row
  const weeks = useMemo(() => {
    const rows = [];
    for (let i = 0; i < days.length; i += 7) {
      rows.push(days.slice(i, i + 7));
    }
    return rows;
  }, [days]);

  const getAppointmentsForDay = (date) => {
    if (!date) return [];
    const dateStr = format(date, 'yyyy-MM-dd');
    return appointments.filter(a => a.date === dateStr);
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/appointments/${id}`, { withCredentials: true });
      toast.success('RDV supprime');
      fetchData();
    } catch (err) {
      console.error('Delete appointment error:', err);
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleDayClick = (date) => {
    setSelectedDate(date);
    setEditingApt(null);
    setDialogOpen(true);
  };

  const todayStr = format(new Date(), 'yyyy-MM-dd');
  const selectedDateApts = selectedDate ? getAppointmentsForDay(selectedDate) : [];

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="calendar-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-white font-['Outfit'] tracking-tight">Calendrier</h1>
          <p className="text-zinc-400 mt-1">Gerez vos rendez-vous</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => { setLoading(true); fetchData(); }} className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-zinc-700 text-zinc-400 hover:text-white hover:border-zinc-500 text-sm transition-colors" data-testid="refresh-button">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Actualiser
          </button>
          <Button className="bg-white text-black hover:bg-zinc-200" onClick={() => { setSelectedDate(new Date()); setEditingApt(null); setDialogOpen(true); }} data-testid="add-appointment-button">
            <Plus className="w-4 h-4 mr-2" /> Nouveau RDV
          </Button>
        </div>
      </div>

      {/* Month Navigation */}
      <Card className="bg-zinc-900 border-zinc-800">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-4">
            <Button variant="ghost" size="icon" onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}>
              <ChevronLeft className="w-5 h-5" />
            </Button>
            <h2 className="text-xl font-semibold text-white font-['Outfit'] capitalize">
              {format(currentMonth, 'MMMM yyyy', { locale: fr })}
            </h2>
            <Button variant="ghost" size="icon" onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}>
              <ChevronRight className="w-5 h-5" />
            </Button>
          </div>

          {/* Day headers */}
          <div className="grid gap-1 mb-1" style={{ gridTemplateColumns: '40px repeat(7, minmax(0, 1fr))' }}>
            <div className="text-center text-[10px] font-bold text-zinc-600 py-2 uppercase">Sem.</div>
            {DAYS.map(d => (
              <div key={d} className="text-center text-xs font-medium text-zinc-500 py-2">{d}</div>
            ))}
          </div>

          {/* Calendar grid with week numbers */}
          <div className="space-y-1">
            {weeks.map((week, wi) => {
              // Find first real date in the week to compute ISO week number
              const refDate = week.find(d => d) || null;
              const weekNum = refDate ? getISOWeek(refDate) : '';
              return (
                <div key={wi} className="grid gap-1" style={{ gridTemplateColumns: '40px repeat(7, minmax(0, 1fr))' }}>
                  <div className="h-24 flex items-start justify-center pt-2 text-xs font-mono text-zinc-500 bg-zinc-800/20 rounded-lg" data-testid={`week-num-${weekNum}`}>
                    S{weekNum}
                  </div>
                  {week.map((date, i) => {
                    if (!date) return <div key={`pad-${wi}-${i}`} className="h-24 bg-zinc-950/30 rounded-lg" />;
                    const dayApts = getAppointmentsForDay(date);
                    const isToday = format(date, 'yyyy-MM-dd') === todayStr;
                    const isCurrentMonth = isSameMonth(date, currentMonth);

                    return (
                      <div
                        key={i}
                        onClick={() => handleDayClick(date)}
                        className={`h-24 rounded-lg p-1.5 cursor-pointer transition-colors border ${
                          isToday ? 'bg-zinc-800/60' :
                          'border-transparent hover:border-zinc-700 bg-zinc-800/30 hover:bg-zinc-800/60'
                        } ${!isCurrentMonth ? 'opacity-40' : ''}`}
                        style={isToday ? { borderColor: 'var(--accent-color, #10B981)' } : undefined}
                      >
                        <div className="text-xs font-medium mb-1"
                          style={{ color: isToday ? 'var(--accent-color, #10B981)' : '#A1A1AA' }}>
                          {format(date, 'd')}
                        </div>
                        <div className="space-y-0.5 overflow-hidden">
                          {dayApts.slice(0, 3).map(apt => (
                            <div
                              key={apt.id}
                              className="text-[10px] px-1 py-0.5 rounded truncate text-white"
                              style={{ backgroundColor: apt.color || '#71717A' }}
                            >
                              {apt.time && <span className="font-mono mr-1">{apt.time}</span>}
                              {apt.title}
                            </div>
                          ))}
                          {dayApts.length > 3 && (
                            <div className="text-[10px] text-zinc-500">+{dayApts.length - 3}</div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg max-h-[90vh] overflow-y-auto w-[95vw]">
          <DialogHeader>
            <DialogTitle className="text-white">
              {editingApt ? 'Modifier le RDV' : selectedDate ? `RDV - ${format(selectedDate, 'dd MMMM yyyy', { locale: fr })}` : 'Nouveau RDV'}
            </DialogTitle>
          </DialogHeader>

          {/* Existing appointments for the day */}
          {!editingApt && selectedDateApts.length > 0 && (
            <div className="space-y-2 mb-4">
              <p className="text-xs text-zinc-500 uppercase tracking-wide">RDV existants</p>
              {selectedDateApts.map(apt => {
                const config = TYPE_CONFIG[apt.type] || TYPE_CONFIG.autre;
                return (
                  <div key={apt.id} className="flex items-center justify-between p-2 bg-zinc-800/50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full" style={{ backgroundColor: config.color }} />
                      <div>
                        <p className="text-sm text-white">{apt.title}</p>
                        <p className="text-xs text-zinc-500">{apt.time}{apt.end_time ? ` - ${apt.end_time}` : ''} | {config.label}</p>
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setEditingApt(apt)}>
                        <Edit className="w-3 h-3 text-zinc-400" />
                      </Button>
                      <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => handleDelete(apt.id)}>
                        <Trash2 className="w-3 h-3 text-red-400" />
                      </Button>
                    </div>
                  </div>
                );
              })}
              <div className="border-t border-zinc-800 my-3" />
              <p className="text-xs text-zinc-500 uppercase tracking-wide">Ajouter un RDV</p>
            </div>
          )}

          <AppointmentForm
            appointment={editingApt || (selectedDate ? { date: format(selectedDate, 'yyyy-MM-dd') } : null)}
            properties={properties}
            onSave={fetchData}
            onClose={() => { setDialogOpen(false); setEditingApt(null); }}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
};
